import { collection, doc, getDoc, getDocs } from "firebase/firestore";
import { initialData } from "../data/initialData";
import { db, isFirebaseConfigured } from "./firebase";
import type { Category, MediaAsset, MenuItem, Restaurant, SiteData } from "../types";

function sortByOrder<T extends { order: number }>(items: T[]): T[] {
  return [...items].sort((a, b) => a.order - b.order);
}

function completeCategoryTree(categories: Category[]): Category[] {
  const categoriesById = new Map(categories.map((category) => [category.id, category]));
  const officialCategoriesById = new Map(initialData.categories.map((category) => [category.id, category]));
  let addedParent = true;

  while (addedParent) {
    addedParent = false;
    for (const category of categoriesById.values()) {
      if (!category.parentId || categoriesById.has(category.parentId)) continue;

      const parent = officialCategoriesById.get(category.parentId);
      if (parent) {
        categoriesById.set(parent.id, parent);
        addedParent = true;
      }
    }
  }

  return sortByOrder([...categoriesById.values()]);
}

export async function loadSiteData(): Promise<SiteData> {
  if (!isFirebaseConfigured || !db) {
    return initialData;
  }

  try {
    const [restaurantSnap, categoriesSnap, menuItemsSnap, mediaSnap] = await Promise.all([
      getDoc(doc(db, "restaurants", "ortaya")),
      getDocs(collection(db, "categories")),
      getDocs(collection(db, "menuItems")),
      getDocs(collection(db, "media"))
    ]);

    const restaurant = restaurantSnap.exists()
      ? ({ id: restaurantSnap.id, ...restaurantSnap.data() } as Restaurant)
      : initialData.restaurant;

    const categories = categoriesSnap.docs
      .map((item) => ({
        id: item.id,
        ...item.data()
      }) as Category)
      .filter((item) => item.active);

    const menuItems = menuItemsSnap.docs
      .map((item) => ({
        id: item.id,
        ...item.data()
      }) as MenuItem)
      .filter((item) => item.active);

    const media = mediaSnap.docs
      .map((item) => ({
        id: item.id,
        ...item.data()
      }) as MediaAsset)
      .filter((item) => item.active);

    return {
      restaurant,
      categories: categories.length ? completeCategoryTree(categories) : initialData.categories,
      menuItems: sortByOrder(menuItems.length ? menuItems : initialData.menuItems),
      media: sortByOrder(media.length ? media : initialData.media)
    };
  } catch (error) {
    console.warn("Firebase-Daten konnten nicht geladen werden. Der veröffentlichte Ausgangskatalog wird verwendet.", error);
    return initialData;
  }
}
